import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listConversations,
  getConversation,
  listMessages,
  createConversation,
  deleteConversation,
  sendMessage,
  generateChatReply,
  type ConversationListItem,
  type ConversationDetail,
  type ChatMessage,
} from '../lib/api'
import { getErrorMessage } from '../lib/errors'
import { toast } from 'sonner'

const CHAT_QUERY_KEY = ['chat'] as const

export type { ConversationListItem, ConversationDetail, ChatMessage }

export function useConversations(limit: number = 20, offset: number = 0) {
  return useQuery({
    queryKey: [...CHAT_QUERY_KEY, 'conversations', limit, offset],
    queryFn: () => listConversations(limit, offset),
  })
}

export function useConversation(conversationId: string | null) {
  return useQuery({
    queryKey: [...CHAT_QUERY_KEY, 'conversation', conversationId],
    queryFn: () => getConversation(conversationId!),
    enabled: !!conversationId,
  })
}

export function useConversationMessages(
  conversationId: string | null,
  limit: number = 50,
  offset: number = 0
) {
  return useQuery({
    queryKey: [...CHAT_QUERY_KEY, 'messages', conversationId, limit, offset],
    queryFn: () => listMessages(conversationId!, limit, offset),
    enabled: !!conversationId,
  })
}

export function useCreateConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (title?: string | null) => createConversation(title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY })
      toast.success('Conversación creada')
    },
    onError: (err) => {
      toast.error('Error al crear la conversación', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useDeleteConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: string) => deleteConversation(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHAT_QUERY_KEY })
      toast.success('Conversación eliminada')
    },
    onError: (err) => {
      toast.error('Error al eliminar la conversación', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      conversationId,
      role,
      content,
    }: {
      conversationId: string
      role: 'user' | 'assistant' | 'system'
      content: string
    }) => sendMessage(conversationId, role, content),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [...CHAT_QUERY_KEY, 'messages', variables.conversationId],
      })
      queryClient.invalidateQueries({
        queryKey: [...CHAT_QUERY_KEY, 'conversation', variables.conversationId],
      })
      queryClient.invalidateQueries({ queryKey: [...CHAT_QUERY_KEY, 'conversations'] })
    },
    onError: (err) => {
      toast.error('Error al enviar el mensaje', {
        description: getErrorMessage(err),
      })
    },
  })
}

export function useGenerateChatReply() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: string) => generateChatReply(conversationId),
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({
        queryKey: [...CHAT_QUERY_KEY, 'messages', conversationId],
      })
    },
    onError: (err) => {
      toast.error('Error al generar respuesta de IA', {
        description: getErrorMessage(err),
      })
    },
  })
}
